import React, { useState, useCallback, memo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { Dialog, DialogActions, DialogTitle, Paper } from '@mui/material'
import { IDialogAutocompleteMobileMenuProps, IDropdownBooleanOption, IDropdownChangeIncomeFieldsOption, IDropdownOption, IInputDropdownInputProps, InputBooleanOptions, InputDropdownProps } from './InputDropdownProps'
import { ReactComponent as ExpandIcon } from '../../../../icons/svg/expand-more.svg'
import { FieldTypes, InputModes } from '../../../../models/form/IFieldsDefinition'
import useIsMobile from '../../../../hooks/useIsMobile'

import './InputDropdown.scss'


/**
 * Menu dropdownlisty na całą szerokość ekranu pojawiające się tylko w widoku mobilnym aplikacji 
*/
const DialogAutocompleteMobileMenu = memo((props: IDialogAutocompleteMobileMenuProps) => {
   return (
      <Dialog
         fullScreen 
         open={true} //Zawsze "otwarty" - komponent Autocomplete steruje pojawianiem się tego komponentu
         disableEnforceFocus //!!! - Ważne - zapobiega rerenderowaniu się komponentu w nieskończonej pętli
         disableAutoFocus //!!! - Ważne - zapobiega rerenderowaniu się komponentu w nieskończonej pętli
         onBlur={event  => {}}
         onFocus={event => {}}
         onClose={props.handleOnMenuClose}
         classes={{
            root: 'input-dropdown-menu-outer-wrapper',
            paper: 'input-dropdown-menu-container-wrapper'
         }} 
      >
         <DialogTitle classes={{root: 'input-dropdown-menu-title'}}>
            <ExpandIcon className="input-dropdown-menu-title-back-icon" onClick={props.handleOnMenuClose}/>
            <p>{props.inputLabel}</p>
         </DialogTitle>
            {props.children}
         <DialogActions classes={{ root: 'input-dropdown-menu-bottom-back-button'}} onClick={props.handleOnMenuClose}>
            <p>Wróć</p>
         </DialogActions>
      </Dialog>
   )
})


/**
 * Komponent kontrolki wyświetlający wartości z Dropdown listy
 * @param props 
 * @returns 
 */
const InputDropdownInput = (props: IInputDropdownInputProps) => {

   return (
      <div className='input-dropdown-dropdown-list-input-wrapper' ref={props.inputRef}>
         <ExpandIcon className='input-dropdown-dropdown-list-input-icon' onClick={() => props.isMobileDevice && props.handleToggleMenuOpen()} /> 
         <input 
            {...props.inputProps} // Atrybuty kontrolki z biblioteki Mui - muszą być jako pierwsze żeby nie nadpisywały pozostałych wartości
            inputMode={InputModes.none} //Dzięki inputMode="none" nie uruchamia się klawiatura ekranowa na urządzeniach moblinych
            id={props.name}
            name={props.name} 
            type="text"
            readOnly={props.isMobileDevice? true : false} //Pozwala "wyłączyć" focus na kontrolce (brak kursora w kontrolce na urządzeniach mobilnych)
            autoFocus={false}
            onPointerDown={(e) => {
                if (props.isMobileDevice) {
                    e.preventDefault();
                }
                (props.inputProps as any).onPointerDown?.(e);
            }}
            onClick={(e) => {
                (props.inputProps as any).onClick?.(e);
                if (props.isMobileDevice) {
                    (document.activeElement as HTMLElement | null)?.blur();
                    props.handleToggleMenuOpen?.();
                }
            }}
            onFocus={(e: React.FocusEvent<HTMLInputElement, Element>) => {
               if(!props.isMobileDevice) {
                  props.inputProps.onFocus && props.inputProps.onFocus(e) // Pozwala poprawić błąd z aktywną ikonką otwarcia listy po wybraniu wartości na urządzeniu mobilnym
               }
            }} 
         />
      </div>
   )
}


const InputDropdown = memo((props: InputDropdownProps) => {
   const [isMenuOpen, setIsMenuOpen] = useState(false)
   const isMobileDevice = useIsMobile()
   const handleOnMenuClose = useCallback(() => setIsMenuOpen(false), [])
   const handleToggleMenuOpen =  useCallback(() => setIsMenuOpen(!isMenuOpen), [isMenuOpen])



   // =============================== HANDLERS ===============================

   const handleOnChange = (e: React.SyntheticEvent, newValue: IDropdownOption | IDropdownBooleanOption | IDropdownChangeIncomeFieldsOption) => {
      switch(props.type){
         case FieldTypes.dropdown: {
            props.handleOnChange(newValue as IDropdownOption)
            break
         }
         case FieldTypes.boolean: {
            props.handleOnChange(newValue as IDropdownBooleanOption)
            break
         }
         case FieldTypes.dropdownChangeIncomeTypeField: {
            props.handleOnChange(newValue as IDropdownChangeIncomeFieldsOption)
            break
         }
         default: {
            console.trace("Nieobsługiwany typ kontrolki")
            return
         }
      }
   }
   // ==========================================================================

   return (
      <Autocomplete         
         disableClearable
         openOnFocus	
         handleHomeEndKeys 
         open={isMenuOpen}
         onOpen={() => setIsMenuOpen(true)}
         onClose={() => setIsMenuOpen(false)}
         isOptionEqualToValue={(option: IDropdownOption, value: IDropdownOption) => option.value === value.value}
         options={props.options}
         inputValue={props.value.label ?? ''}
         getOptionLabel={(option: IDropdownOption) => option.label}
         filterOptions={(options) => options} // Zawsze zwracamy pełną listę opcji, nie filtrujemy ich
         onChange={handleOnChange}
         classes={{ 
            root:    'input-dropdown-dropdown-input-root',
            paper:   'input-dropdown-dropdown-list-outer-wrapper', 
            option:  'input-dropdown-dropdown-list-option',
            listbox: 'input-dropdown-dropdown-list-inner-wrapper input-dropdown-dropdown-list-remove-scrollbar',
            input:   'input-dropdown-dropdown-list-input'
         }}
         renderOption={(props, option) => {
            if(typeof option.value !== 'boolean'){
               return <li {...props} data-test-id={option.value}>{option.label}</li>
            }
            else {
               return (
                  <li 
                     {...props}
                     data-test-id={option.label === InputBooleanOptions.agree? 'input-dropdown-agree-option' : 'input-dropdown-disagree-option'}
                  >
                     {option.label}
                  </li>
               ) 
            }
         }}
         renderInput={(params) => (
            <InputDropdownInput 
               name={props.name} 
               inputProps={params.inputProps} 
               isMobileDevice={isMobileDevice} 
               inputRef={params.InputProps.ref}
               handleToggleMenuOpen={handleToggleMenuOpen}
            />
         )}
         PaperComponent={(muiProps) => {
            return (
               isMobileDevice ? 
               <DialogAutocompleteMobileMenu  //Menu używane w wersji mobilnej
                  isMenuOpen={isMenuOpen}
                  children={muiProps.children}
                  inputLabel={props.inputLabel}
                  handleOnMenuClose={handleOnMenuClose}
               /> 
               :
               <Paper className='input-dropdown-dropdown-list-outer-wrapper'>  {/* Menu używane w wersji desktopowej */}
                  {muiProps.children}
               </Paper>
            )
         }} 
      />
   )
})

export default InputDropdown